import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AZURE } from '../services/api';

/**
 * Access Guard - Verifica account_status e saas_status do usuário
 * OPÇÃO A: Apenas para tokens antigos (cinto e suspensório)
 *
 * Casos tratados:
 * - Token antigo (usuário foi aprovado, depois voltou a pending/suspended)
 * - Assinatura Azure Marketplace suspensa (saas_status = 'Suspended')
 * - Proteção adicional caso middleware falhe
 */
export const useAccessGuard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checkComplete, setCheckComplete] = useState(false);

  useEffect(() => {
    const checkAccessStatus = async () => {
      const BYPASS_PATHS = ['/pending-approval', '/subscription-suspended'];

      // Não verifica se não estiver logado ou já estiver numa página de bloqueio
      if (!user || BYPASS_PATHS.includes(location.pathname)) {
        setCheckComplete(true);
        return;
      }

      // Skip verification for mock tokens (dev only)
      if (user.token && user.token.startsWith('mock-token-')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AccessGuard] ⏭️ Skipping check for mock token');
        }
        setCheckComplete(true);
        return;
      }

      try {
        console.log('[AccessGuard] Verificando account_status e saas_status...');

        // Chama /api/auth/me para verificar se token ainda é válido
        const { data } = await AZURE.get('/auth/me');

        if (data.success && data.data) {
          const accountStatus    = data.data.account_status  || 'pending';
          const onboardingStatus = data.data.onboarding_status;
          const saasStatus       = data.data.saas_status;   // null = sem assinatura Marketplace

          console.log(`[AccessGuard] account_status=${accountStatus}, onboarding_status=${onboardingStatus}, saas_status=${saasStatus}`);

          // 1. Assinatura Marketplace suspensa — bloqueia sem logout
          if (saasStatus === 'Suspended') {
            console.log('[AccessGuard] ⛔ Assinatura Marketplace SUSPENSA — redirecionando');
            navigate('/subscription-suspended', { replace: true });
            return;
          }

          // 2. Allow club_admin with pending onboarding through — CentralGate handles redirect
          if (onboardingStatus === 'pending') {
            console.log('[AccessGuard] ✅ Onboarding pending — allowing through for wizard redirect');
            setCheckComplete(true);
            return;
          }

          // 3. Bloqueia se não for approved/active (token antigo)
          if (accountStatus !== 'approved' && accountStatus !== 'active') {
            console.log(`[AccessGuard] ⛔ Token antigo detectado - status ${accountStatus} - fazendo logout`);

            localStorage.setItem('arena_stats_pending_user', JSON.stringify({
              email: user.email,
              account_status: accountStatus
            }));

            logout();
            navigate('/pending-approval', { replace: true });
            return;
          }

          console.log('[AccessGuard] ✅ Token válido - acesso liberado');
        }
      } catch (error) {
        console.error('[AccessGuard] Erro ao verificar status:', error);

        // Se erro 403 (ACCOUNT_AWAITING_LAUNCH), faz logout
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_AWAITING_LAUNCH') {
          const accountStatus = error.response.data.account_status || 'pending';

          localStorage.setItem('arena_stats_pending_user', JSON.stringify({
            email: user.email,
            account_status: accountStatus
          }));

          logout();
          navigate('/pending-approval', { replace: true });
          return;
        }

        // Se erro 401 (token inválido), faz logout
        if (error.response?.status === 401) {
          console.log('[AccessGuard] Token inválido - fazendo logout');
          logout();
          navigate('/login', { replace: true });
          return;
        }
      } finally {
        setCheckComplete(true);
      }
    };

    checkAccessStatus();
  }, [user, navigate, location.pathname, logout]);

  return { checkComplete };
};

/**
 * Componente Guard - Wrap children e verifica acesso
 * Mostra loading enquanto verifica
 */
export const AccessGuard = ({ children }) => {
  const { checkComplete } = useAccessGuard();

  if (!checkComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-12 h-12 border-4 border-t-green-700 border-gray-200 rounded-full"></div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AccessGuard;
